import { useEffect, useRef, type RefObject } from 'react';

// @ai-generated 2026-09-21T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5

/** The two inputs of the automation, so that the one can hand the cursor to the other. */
export interface LocalityHandover {
    /** The postal code field's input — where the cursor is handed away from. */
    zipInputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | undefined>;
    /** The city field's input — where the cursor is handed to, and where the list then opens. */
    cityInputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | undefined>;
}

/**
 * Puts the cursor in the city field once a postal code turns out to have several localities, so
 * that the field's suggestion list opens on the choice it is about to ask for.
 *
 * That moment is the only one where this is a help rather than an interruption: the postal code is
 * finished, the user is still looking at it, and the next thing to do is settle which of its places
 * it is. Whoever has already moved on keeps the cursor wherever they put it.
 *
 * The two halves of that moment do not arrive together. The answer comes first; where it empties
 * the field, that reaches the input a render or two later. So the cursor waits for the field to
 * show what the answer makes of it: the suggestion list narrows to what the input holds, and a
 * cursor arriving early would open it on a city that is already gone.
 *
 * Whatever it then finds there arrives selected, so the first keystroke replaces it and the list
 * follows from that letter. An emptied field and an entry the automation may not touch come to the
 * same thing that way, which is the point — one keystroke, the same result, whichever it was.
 */
export function useLocalityHandover(hasChoiceToMake: boolean, cityWhenSettled: string): LocalityHandover {
    const zipInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | undefined>(undefined);
    const cityInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | undefined>(undefined);

    /**
     * A hand-over owed, from the answer that holds a choice until the field is free for it. One per
     * answer: every postal code passes through a lookup that offers nothing — `idle` while it is
     * incomplete, `searching` while the answer to the one before it is stale — so coming back to
     * the postal code field, or any other render, finds this exactly as it left it.
     */
    const isOwed = useRef(false);

    useEffect(() => {
        isOwed.current = hasChoiceToMake;
    }, [hasChoiceToMake]);

    // Deliberately without dependencies: what it waits for is the input's own value, and no
    // dependency reports that — the field catches up with the record a render after it changes.
    useEffect(() => {
        if (!isOwed.current) {
            return;
        }
        if (document.activeElement !== zipInputRef.current) {
            // Somebody else has the cursor, and taking it from them is an interruption whenever it
            // happens — while the answer arrives, or while the field is still coming free.
            isOwed.current = false;
            return;
        }

        const cityInput = cityInputRef.current;
        if (cityInput === undefined || cityInput.value !== cityWhenSettled) {
            // The field is still showing what this answer is about to take out of it.
            return;
        }

        isOwed.current = false;
        cityInput.focus();
        // A no-op on the field the answer emptied, and the whole entry on one it left standing.
        cityInput.select();
    });

    return { zipInputRef, cityInputRef };
}
